import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import BordFloatIsland from 'components/BordFloatIsland/BordFloatIsland';
import ProductModuleListingCard from '../ProductModuleListingCard/ProductModuleListingCard';
import ToolsBulkStatusChangeModal from '../ToolsBulkStatusChangeModal/ToolsBulkStatusChangeModal';
import { IToolData } from '../../../../../types/products';
import { getToolsListing } from '../../../../../services/toolsModule';
import { ICountryDetail } from '../../../../../types/countries';
import ProductsListCardSkeletonLoader from '../ProductsListCardSkeletonLoader/ProductsListCardSkeletonLoader';
import useDebounce from '../../../../../hooks/useDebounce';
import useFiltersState from '../../../../../state/useFiltersState';
import ProductDetailModal from '../ProductDetailModal/ProductDetailModal';
import { LIST_PRODUCTS_TYPE } from '../../../../../types/orders';
import {
  buildToolsBulkSelectionItem,
  IToolsBulkSelectionState,
  isToolSelectedInBulkState,
  mapBulkSelectedTools,
  removeBulkSelectedToolStateItem,
  toggleBulkSelectedToolState
} from '../../productsModuleListing.utils';

import './ProductsListingTableBody.scss';

interface IProductsListingTableBodyProps {
  searchText: string;
  listOfCountries: ICountryDetail[];
}

/**
 * Componente para el body de la tabla de productos que implementa scroll infinito
 */
const ProductsListingTableBody = ({ searchText, listOfCountries }: IProductsListingTableBodyProps) => {
  const [isProductDetailModalOpen, setIsProductDetailModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [bulkSelectedToolsState, setBulkSelectedToolsState] = useState<IToolsBulkSelectionState>({
    order: [],
    byId: {}
  });
  const [isBulkStatusModalOpen, setIsBulkStatusModalOpen] = useState(false);
  const [selectedBulkStatusId, setSelectedBulkStatusId] = useState('');
  const [bulkStatusChangeReason, setBulkStatusChangeReason] = useState('');
  const [bulkStatusEvidenceFile, setBulkStatusEvidenceFile] = useState<File | undefined>();
  const { productsModuleListingFilters, setProductsModuleListingFilters } = useFiltersState();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isListNearBottom, setIsListNearBottom] = useState(false);
  const debouncedSearch = useDebounce(searchText, 800);

  // Esta función retorna la key de cada página,
  // su valor será aceptado por `fetcher`.
  // Si `null` es retornado, la solicitud de esa página no comenzará pues se habrá llegado al final de la lista.
  const getKey = (pageIndex: number, previousPageData: IToolData[]) => {
    if (previousPageData && !previousPageData.length) return null;
    const searchParam = debouncedSearch ? `&search=${debouncedSearch}` : '';
    const filters = productsModuleListingFilters.reduce((acc: { [key: string]: string[] }, filter) => {
      acc[filter.name] = filter.values;
      return acc;
    }, {} as { [key: string]: string[] });
    const filtersParam = Object.keys(filters).length ? `&filters=${JSON.stringify(filters)}` : '';
    return `/backoffice-tools-view?page=${pageIndex}&limit=100&productType=clients${searchParam}${filtersParam}`;
  };

  const {
    data: pages = [],
    isLoading,
    isValidating,
    size,
    setSize,
    mutate: refreshToolsListing
  } = useSWRInfinite(getKey, getToolsListing, {
    revalidateFirstPage: false,
    revalidateOnFocus: false
  });

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const threshold = 100; // píxeles antes del final
    const isNearingBottom = scrollHeight - scrollTop - clientHeight < threshold;

    setIsListNearBottom(isNearingBottom);
  }, []);

  const handleProductDetailModal = (productId: number) => {
    setIsProductDetailModalOpen(true);
    setSelectedProductId(productId);
  };

  const toggleBulkToolSelection = useCallback((product: IToolData) => {
    const snapshot = buildToolsBulkSelectionItem(product);
    if (!snapshot) return;

    setBulkSelectedToolsState(prev => toggleBulkSelectedToolState(prev, snapshot));
  }, []);

  const resetBulkStatusForm = useCallback(() => {
    setSelectedBulkStatusId('');
    setBulkStatusChangeReason('');
    setBulkStatusEvidenceFile(undefined);
  }, []);

  const resetBulkSelection = useCallback(() => {
    setIsBulkStatusModalOpen(false);
    setBulkSelectedToolsState({ order: [], byId: {} });
    resetBulkStatusForm();
  }, [resetBulkStatusForm]);

  const unselectBulkTool = useCallback((toolId: number) => {
    setBulkSelectedToolsState(prev => removeBulkSelectedToolStateItem(prev, toolId));
  }, []);

  const selectedTools = useMemo(
    () => mapBulkSelectedTools(bulkSelectedToolsState),
    [bulkSelectedToolsState]
  );

  const handleBulkStatusUpdateSuccess = useCallback(() => {
    resetBulkSelection();
    void refreshToolsListing();
  }, [resetBulkSelection, refreshToolsListing]);

  const handleCloseProductDetailModal = useCallback(() => {
    setIsProductDetailModalOpen(false);
  }, []);

  const handleOpenBulkStatusModal = useCallback(() => {
    setIsBulkStatusModalOpen(true);
  }, []);

  const handleCloseBulkStatusModal = useCallback(() => {
    setIsBulkStatusModalOpen(false);
  }, []);

  const getBulkSelectionProps = useCallback((product: IToolData) => {
    return {
      isSelected: isToolSelectedInBulkState(bulkSelectedToolsState, product.id),
      onToggle: () => toggleBulkToolSelection(product)
    };
  }, [bulkSelectedToolsState, toggleBulkToolSelection]);

  const tools = pages.flat();
  const availableCountries = listOfCountries || [];
  const selectedToolsCount = bulkSelectedToolsState.order.length;
  const shouldShowLoadingSkeletons = isLoading || isValidating;
  const shouldShowBulkActions = selectedToolsCount > 0;

  const productDetailModal = isProductDetailModalOpen && selectedProductId !== null ? (
    <ProductDetailModal
      productIdSelected={selectedProductId}
      isOpen={isProductDetailModalOpen}
      onClose={handleCloseProductDetailModal}
      listOfCountries={availableCountries}
      refreshToolsListing={refreshToolsListing}
    />
  ) : null;

  const bulkStatusModal = (
    <ToolsBulkStatusChangeModal
      open={isBulkStatusModalOpen}
      onClose={handleCloseBulkStatusModal}
      selectedTools={selectedTools}
      selectedStatusId={selectedBulkStatusId}
      statusChangeReason={bulkStatusChangeReason}
      statusEvidenceFile={bulkStatusEvidenceFile}
      onUnselectTool={unselectBulkTool}
      onSelectedStatusIdChange={setSelectedBulkStatusId}
      onStatusChangeReasonChange={setBulkStatusChangeReason}
      onStatusEvidenceFileChange={setBulkStatusEvidenceFile}
      onBulkStatusUpdated={handleBulkStatusUpdateSuccess}
    />
  );

  const toolCards = tools.map((product: IToolData) => (
    <ProductModuleListingCard
      key={product.id}
      product={product}
      listOfCountries={availableCountries}
      handleModalSide={handleProductDetailModal}
      listProductsType={LIST_PRODUCTS_TYPE.CLIENTS}
      bulkSelection={getBulkSelectionProps(product)}
    />
  ));

  const loadingSkeletons = shouldShowLoadingSkeletons
    ? Array.from({ length: 20 }, (_, index) => (
      <ProductsListCardSkeletonLoader key={index} listProductsType={LIST_PRODUCTS_TYPE.CLIENTS} />
    ))
    : null;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const canLoadMore = isListNearBottom && !isLoading && !isValidating && Boolean(pages[pages.length - 1]?.length);
    if (canLoadMore) {
      setSize(size + 1);
    }
  }, [isListNearBottom, isLoading, isValidating, pages, setSize, size]);

  useEffect(() => {
    return () => {
      setProductsModuleListingFilters([]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedToolsCount !== 0) return;

    setIsBulkStatusModalOpen(false);
    resetBulkStatusForm();
  }, [selectedToolsCount, resetBulkStatusForm]);

  return (
    <div ref={containerRef} className="productsListingTableBody">
      {loadingSkeletons}
      {toolCards}
      {productDetailModal}
      {shouldShowBulkActions && (
        <BordFloatIsland
          leftContentCount={selectedToolsCount}
          leftContentText="Herramientas seleccionadas"
          buttonText="Cambiar estado"
          buttonIcon=""
          onClose={resetBulkSelection}
          onButtonClick={handleOpenBulkStatusModal}
          modal={bulkStatusModal}
          isModalOpen={isBulkStatusModalOpen}
        />
      )}
    </div>
  );
};

export default ProductsListingTableBody;
